// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-18) — where the highest and lowest are looked for:
 * everywhere, on land only, or at sea only
 * (docs/AYNI_XR_SURFACE_EXTREMES_PLAN.md). At Barcelona every lowest
 * temperature fell on the sea; "Land only" keeps the pins ashore.
 *
 * The land and sea come from tiles of our own (`/api/map/city/<pack>/water/
 * z/x/y.png`, built by the VPS: handoff-vps-water-tiles.md). Each tile pixel
 * holds the share of its area that is land (red), inland water (green: lakes,
 * rivers, reservoirs, ponds, canals) and sea (blue), out of 255; wetlands
 * count as neither, so the three can sum to less; alpha 0 where the source
 * says nothing. Catalonia comes from ICGC's 1 m land cover map at zooms 9–14,
 * the rest of the world from Natural Earth at 0–8. A pack may leave out a
 * tile of one class throughout: the nearest ancestor stands in.
 *
 * The mask is made once per dataset grid: each texel sampled at the zoom
 * whose pixels are about its size, and counted as land (or sea) only when at
 * least {@link PURE_SHARE} of it is, so a texel that straddles the shore is
 * neither. The datasets are equirectangular (owner's rule), so a column has
 * one longitude and a row one latitude.
 */

import type { MessageKey } from '../../i18n'

/** Where the extremes are looked for. */
export type SurfaceMode = 'all' | 'land' | 'sea'

export const SURFACE_MODES: readonly SurfaceMode[] = ['all', 'land', 'sea']

/** A choice's own name, as the Data tab and the headset print it. */
export function surfaceLabelKey(mode: SurfaceMode): MessageKey {
  return mode === 'land' ? 'tools.data.pins.surface.land' : mode === 'sea' ? 'tools.data.pins.surface.sea' : 'tools.data.pins.surface.all'
}

/** A texel's class in a mask. */
export const SURFACE = { unknown: 0, land: 1, sea: 2, water: 3, mixed: 4 } as const

/** The share of a texel that must be land (or sea) for it to count as such. Owner, 2026-10-18: 90 %. */
export const PURE_SHARE = 0.9

/** Web-map tiles stop here; beyond, the Arctic is sea and the Antarctic land. */
const MERCATOR_LAT = 85.0511

export interface WaterPack {
  readonly id: string
  /** West, south, east, north. */
  readonly bounds: readonly [number, number, number, number]
  readonly minzoom: number
  readonly maxzoom: number
}

/** The packs, finest first: a texel takes the first that covers it at the zoom it wants. */
export const WATER_PACKS: readonly WaterPack[] = [
  { id: 'catalonia', bounds: [0.15, 40.5, 3.35, 42.9], minzoom: 9, maxzoom: 14 },
  { id: 'world', bounds: [-180, -MERCATOR_LAT, 180, MERCATOR_LAT], minzoom: 0, maxzoom: 8 },
]

/** One tile's RGBA, 256 × 256, or null when there is none. */
export type WaterTileSource = (pack: string, z: number, x: number, y: number) => Promise<Uint8ClampedArray | null>

const TILE = 256

/** A tile pixel's class. */
export function classifyPixel(r: number, g: number, b: number, a: number): number {
  if (a < 128) return SURFACE.unknown
  const pure = PURE_SHARE * 255
  if (r >= pure) return SURFACE.land
  if (b >= pure) return SURFACE.sea
  if (g >= pure) return SURFACE.water
  return SURFACE.mixed
}

/** Whether a class is looked at in a mode. */
export function allowedIn(mode: SurfaceMode, cls: number): boolean {
  if (mode === 'all') return true
  return mode === 'land' ? cls === SURFACE.land : cls === SURFACE.sea
}

/** The tile zoom whose pixels are about `deg` degrees of longitude wide (never finer). */
export function maskZoom(deg: number): number {
  if (!(deg > 0)) return 0
  return Math.max(0, Math.floor(Math.log2(360 / (TILE * deg))))
}

/** A place's web-map pixel at zoom `z` (global, so tile = floor(p / 256)). */
export function worldPixel(lat: number, lon: number, z: number): { px: number; py: number } {
  const n = TILE * 2 ** z
  const l = ((((lon + 180) % 360) + 360) % 360) / 360
  const phi = (Math.max(-MERCATOR_LAT, Math.min(MERCATOR_LAT, lat)) * Math.PI) / 180
  const y = (1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2
  return { px: Math.min(n - 1, Math.floor(l * n)), py: Math.min(n - 1, Math.max(0, Math.floor(y * n))) }
}

/** The texels to classify: a box of a frame, with each column's longitude and each row's latitude. */
export interface MaskGrid {
  readonly width: number
  readonly height: number
  readonly x0: number
  readonly y0: number
  readonly x1: number
  readonly y1: number
  /** Indexed by column (`x`), `width` long. */
  readonly lonOf: Float64Array
  /** Indexed by row (`y`), `height` long. */
  readonly latOf: Float64Array
}

/** A grid from a texel-to-place function, read along one row and one column. */
export function gridOf(
  width: number,
  height: number,
  box: { x0: number; y0: number; x1: number; y1: number },
  placeOf: (x: number, y: number) => { lat: number; lon: number },
): MaskGrid {
  const lonOf = new Float64Array(width)
  const latOf = new Float64Array(height)
  const midY = Math.floor((box.y0 + box.y1) / 2)
  const midX = Math.floor((box.x0 + box.x1) / 2)
  for (let x = box.x0; x < box.x1; x++) lonOf[x] = placeOf(x, midY).lon
  for (let y = box.y0; y < box.y1; y++) latOf[y] = placeOf(midX, y).lat
  return { width, height, ...box, lonOf, latOf }
}

export interface SurfaceMask {
  /** A class per texel of the frame (`SURFACE`), unknown outside the box. */
  readonly classes: Uint8Array
  /** The share of the box's texels the tiles said something about. */
  readonly known: number
  /** The zoom each pack was read at. */
  readonly zooms: Readonly<Record<string, number>>
}

interface Plan {
  pack: WaterPack
  z: number
  colPx: Int32Array
  rowPy: Int32Array
}

/**
 * The land and sea under each texel of `grid`. Null when the tiles say
 * nothing at all (none built yet, or the route unreachable). `maxTiles` caps
 * the tiles a pack reads: a finer grid than that reads a zoom coarser.
 */
export async function buildSurfaceMask(
  grid: MaskGrid,
  source: WaterTileSource,
  packs: readonly WaterPack[] = WATER_PACKS,
  maxTiles = 400,
): Promise<SurfaceMask | null> {
  const { width, x0, y0, x1, y1, lonOf, latOf } = grid
  if (x1 <= x0 || y1 <= y0) return null
  const steps: number[] = []
  for (let x = x0 + 1; x < x1; x++) steps.push(Math.abs(lonOf[x] - lonOf[x - 1]))
  steps.sort((a, b) => a - b)
  const want = maskZoom(steps[Math.floor(steps.length / 2)] ?? 360)

  // Which pack each texel falls to: the first that covers it at the zoom wanted.
  const usable = packs.filter((p) => want >= p.minzoom || p === packs[packs.length - 1])
  const covers = (p: WaterPack, lat: number, lon: number) => lon >= p.bounds[0] && lon <= p.bounds[2] && lat >= p.bounds[1] && lat <= p.bounds[3]
  const packOf = new Int8Array(grid.width * grid.height).fill(-1)
  for (let y = y0; y < y1; y++) {
    const lat = latOf[y]
    for (let x = x0; x < x1; x++) {
      const lon = lonOf[x]
      const i = usable.findIndex((p) => covers(p, lat, lon))
      packOf[y * width + x] = i
    }
  }

  // Each pack's zoom, coarser until its tiles fit `maxTiles`, and the tiles it needs.
  const plans: Plan[] = []
  const needed: Array<Set<string>> = []
  for (let k = 0; k < usable.length; k++) {
    const pack = usable[k]
    let z = Math.max(pack.minzoom, Math.min(want, pack.maxzoom))
    for (;;) {
      const colPx = new Int32Array(width)
      const rowPy = new Int32Array(grid.height)
      for (let x = x0; x < x1; x++) colPx[x] = worldPixel(0, lonOf[x], z).px
      for (let y = y0; y < y1; y++) rowPy[y] = worldPixel(latOf[y], 0, z).py
      const tiles = new Set<string>()
      for (let y = y0; y < y1; y++) {
        const ty = rowPy[y] >> 8
        for (let x = x0; x < x1; x++) if (packOf[y * width + x] === k && Math.abs(latOf[y]) <= MERCATOR_LAT) tiles.add(`${colPx[x] >> 8},${ty}`)
      }
      if (tiles.size <= maxTiles || z <= pack.minzoom) {
        plans.push({ pack, z, colPx, rowPy })
        needed.push(tiles)
        break
      }
      z--
    }
  }

  // Fetch, and for a tile that is not there, its nearest ancestor in the pack.
  // Kept as promises, so tiles sharing an ancestor read it once.
  const got = new Map<string, Promise<Uint8ClampedArray | null>>()
  const fetchOne = (pack: string, z: number, x: number, y: number): Promise<Uint8ClampedArray | null> => {
    const key = `${pack}/${z}/${x}/${y}`
    let held = got.get(key)
    if (!held) {
      held = source(pack, z, x, y).catch(() => null)
      got.set(key, held)
    }
    return held
  }
  const resolved = new Map<string, { data: Uint8ClampedArray; shift: number; ox: number; oy: number }>()
  const jobs: Array<() => Promise<void>> = []
  plans.forEach((plan, k) => {
    for (const t of needed[k]) {
      const [tx, ty] = t.split(',').map(Number)
      jobs.push(async () => {
        for (let s = 0; plan.z - s >= plan.pack.minzoom; s++) {
          const data = await fetchOne(plan.pack.id, plan.z - s, tx >> s, ty >> s)
          if (data) {
            resolved.set(`${k}:${t}`, { data, shift: s, ox: (tx - ((tx >> s) << s)) * TILE, oy: (ty - ((ty >> s) << s)) * TILE })
            return
          }
        }
      })
    }
  })
  let next = 0
  await Promise.all(Array.from({ length: 8 }, async () => {
    while (next < jobs.length) await jobs[next++]()
  }))
  if (resolved.size === 0) return null

  const classes = new Uint8Array(grid.width * grid.height)
  let known = 0
  for (let y = y0; y < y1; y++) {
    const lat = latOf[y]
    for (let x = x0; x < x1; x++) {
      const i = y * width + x
      let cls: number = SURFACE.unknown
      if (lat > MERCATOR_LAT) cls = SURFACE.sea
      else if (lat < -MERCATOR_LAT) cls = SURFACE.land
      else {
        const k = packOf[i]
        const plan = plans[k]
        if (plan) {
          const px = plan.colPx[x]
          const py = plan.rowPy[y]
          const tile = resolved.get(`${k}:${px >> 8},${py >> 8}`)
          if (tile) {
            const ax = ((px & 255) + tile.ox) >> tile.shift
            const ay = ((py & 255) + tile.oy) >> tile.shift
            const o = (ay * TILE + ax) * 4
            cls = classifyPixel(tile.data[o], tile.data[o + 1], tile.data[o + 2], tile.data[o + 3])
          }
        }
      }
      classes[i] = cls
      if (cls !== SURFACE.unknown) known++
    }
  }
  const zooms: Record<string, number> = {}
  plans.forEach((p, k) => { if (needed[k].size) zooms[p.pack.id] = p.z })
  return { classes, known: known / ((x1 - x0) * (y1 - y0)), zooms }
}

/** The texels a mode looks at (1) and leaves (0), or null for "everywhere". */
export function allowedTexels(mask: SurfaceMask, mode: SurfaceMode): Uint8Array | null {
  if (mode === 'all') return null
  const out = new Uint8Array(mask.classes.length)
  for (let i = 0; i < out.length; i++) out[i] = allowedIn(mode, mask.classes[i]) ? 1 : 0
  return out
}

/** Below this share of texels known, a mask is "not here yet". */
export const MIN_KNOWN = 0.5

/** What a frame's mask is: being made, made, or not to be had. */
export type MaskState =
  | { status: 'loading' }
  | { status: 'none' }
  | { status: 'ready'; mask: SurfaceMask }

/**
 * The masks made so far, one per dataset grid (`key`), each made once;
 * `onReady` fires when one finishes, so following pins can look again.
 */
export class SurfaceMasks {
  private readonly states = new Map<string, MaskState>()
  private readonly byMode = new Map<string, Uint8Array | null>()

  constructor(private readonly source: WaterTileSource, private readonly onReady: () => void = () => {}) {}

  /** Where `key`'s mask stands, without starting it; null when it was never asked for. */
  peek(key: string): MaskState['status'] | null {
    return this.states.get(key)?.status ?? null
  }

  /** The mask for `key`, starting it from `grid()` the first time it is asked for. */
  state(key: string, grid: () => MaskGrid | null): MaskState {
    const known = this.states.get(key)
    if (known) return known
    const g = grid()
    if (!g) return { status: 'none' }
    this.states.set(key, { status: 'loading' })
    void buildSurfaceMask(g, this.source).then((mask) => {
      this.states.set(key, mask && mask.known >= MIN_KNOWN ? { status: 'ready', mask } : { status: 'none' })
    }, () => {
      this.states.set(key, { status: 'none' })
    }).finally(() => this.onReady())
    return { status: 'loading' }
  }

  /** The texels `mode` looks at on `key`'s grid, or the mask's state when it is not ready. */
  allowed(key: string, grid: () => MaskGrid | null, mode: SurfaceMode): Uint8Array | null | 'loading' | 'none' {
    if (mode === 'all') return null
    const s = this.state(key, grid)
    if (s.status !== 'ready') return s.status
    const k = `${key}|${mode}`
    if (!this.byMode.has(k)) this.byMode.set(k, allowedTexels(s.mask, mode))
    return this.byMode.get(k)!
  }
}

/** The tiles through our map route, decoded without colour management (the values are shares, not colours). */
export const httpWaterTiles: WaterTileSource = async (pack, z, x, y) => {
  const res = await fetch(`/api/map/city/${pack}/water/${z}/${x}/${y}.png`)
  if (!res.ok) return null
  const bitmap = await createImageBitmap(await res.blob(), { premultiplyAlpha: 'none', colorSpaceConversion: 'none' })
  const canvas = new OffscreenCanvas(TILE, TILE)
  const g = canvas.getContext('2d', { willReadFrequently: true })
  if (!g) return null
  g.drawImage(bitmap, 0, 0, TILE, TILE)
  bitmap.close()
  return g.getImageData(0, 0, TILE, TILE).data
}
